import React from 'react';
import LegalPage from './LegalPage';

const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

// TODO: update when company is incorporated
const PRIVACY_EMAIL = 'privacy@jyto.eu';

const s = {
  lead:   { fontSize: 16, lineHeight: 1.75, color: INK2, margin: '0 0 40px', paddingBottom: 40, borderBottom: `1px solid ${RULE}` },
  h2:     { fontSize: 19, fontWeight: 700, color: INK, margin: '40px 0 12px', letterSpacing: '-0.02em', paddingTop: 40, borderTop: `1px solid ${RULE}` },
  h2top:  { fontSize: 19, fontWeight: 700, color: INK, margin: '0 0 12px', letterSpacing: '-0.02em' },
  h3:     { fontSize: 15, fontWeight: 600, color: INK, margin: '24px 0 8px' },
  p:      { fontSize: 15, lineHeight: 1.75, color: INK2, margin: '0 0 16px' },
  ul:     { fontSize: 15, lineHeight: 1.75, color: INK2, paddingLeft: 24, margin: '0 0 16px' },
  li:     { marginBottom: 6 },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '16px 0 24px', display: 'block', overflowX: 'auto' },
  th:     { textAlign: 'left', padding: '8px 12px', background: '#F6F7FB', borderBottom: `1px solid ${RULE}`, color: INK, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  td:     { padding: '10px 12px', borderBottom: `1px solid ${RULE}`, color: INK2, verticalAlign: 'top' },
  note:   { background: '#F6F7FB', border: `1px solid ${RULE}`, borderRadius: 8, padding: '16px 20px', margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: INK2 },
  a:      { color: ACC, textDecoration: 'none', fontWeight: 500 },
};

export default function CookiePolicy() {
  return (
    <LegalPage title="Cookie policy" lastUpdated="9 May 2026">

      <p style={s.lead}>
        This Cookie Policy explains how Jyto B.V. ("<strong>Jyto</strong>", "<strong>we</strong>")
        uses cookies and similar browser storage technologies when you use our platform. We have designed
        Jyto to minimise the use of cookies — this page is intentionally short.
      </p>

      <h2 style={s.h2top}>What are cookies?</h2>
      <p style={s.p}>
        Cookies are small text files that a website stores on your device. Similar technologies include
        browser <strong>localStorage</strong> and <strong>sessionStorage</strong>, which store data
        locally in your browser rather than as cookies sent with each HTTP request.
      </p>

      <h2 style={s.h2}>How Jyto uses browser storage</h2>
      <div style={s.note}>
        Jyto does <strong>not</strong> use cookies for authentication. Instead, we store your
        authentication token in your browser's <strong>localStorage</strong>. This token keeps you
        logged in between sessions and is cleared automatically when you sign out. It is never sent
        to third parties.
      </div>

      <h3 style={s.h3}>localStorage (first-party)</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Key</th>
            <th style={s.th}>Purpose</th>
            <th style={s.th}>Expiry</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['auth_token',       'Stores your JWT authentication token to keep you logged in', 'Cleared on sign-out; expires after 7 days'],
            ['UI preferences',   'Stores lightweight UI state (e.g. sidebar preferences)',     'Session or indefinite'],
          ].map(([key, purpose, expiry]) => (
            <tr key={key}>
              <td style={{ ...s.td, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}>{key}</td>
              <td style={s.td}>{purpose}</td>
              <td style={s.td}>{expiry}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={s.h2}>Third-party cookies</h2>
      <p style={s.p}>
        When you proceed to checkout or manage your subscription, you are redirected to a Stripe-hosted
        payment page. Stripe may set its own cookies on that page for fraud prevention, payment security,
        and session management purposes. Jyto does not control Stripe's cookie behaviour.
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Third party</th>
            <th style={s.th}>When active</th>
            <th style={s.th}>Purpose</th>
            <th style={s.th}>More info</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={s.td}><strong>Stripe Inc.</strong></td>
            <td style={s.td}>During payment checkout</td>
            <td style={s.td}>Fraud prevention, payment session management</td>
            <td style={s.td}>stripe.com/privacy</td>
          </tr>
        </tbody>
      </table>

      <h2 style={s.h2}>What we do not use</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Analytics cookies</strong> — we do not use Google Analytics, Segment, Mixpanel, or similar tracking tools</li>
        <li style={s.li}><strong>Advertising cookies</strong> — we do not serve ads or use advertising networks</li>
        <li style={s.li}><strong>Social media tracking pixels</strong> — we do not use Facebook Pixel or equivalent tools</li>
      </ul>

      <h2 style={s.h2}>How to manage your browser storage and cookies</h2>

      <h3 style={s.h3}>Clearing localStorage</h3>
      <p style={s.p}>
        You can clear localStorage through your browser's developer tools (Settings → Application → Local Storage
        in Chrome/Edge, or Storage Inspector in Firefox). Note that clearing localStorage will sign you out
        of Jyto.
      </p>

      <h3 style={s.h3}>Managing cookies</h3>
      <p style={s.p}>
        You can block or delete cookies through your browser settings. The steps vary by browser:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
        <li style={s.li}><strong>Firefox:</strong> Settings → Privacy &amp; Security → Cookies and Site Data</li>
        <li style={s.li}><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
        <li style={s.li}><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
      </ul>
      <p style={s.p}>
        Blocking all third-party cookies may affect the Stripe checkout experience but will not prevent
        you from using the core Jyto platform.
      </p>

      <h2 style={s.h2}>Changes to this policy</h2>
      <p style={s.p}>
        We will update this Cookie Policy if our use of cookies or browser storage changes materially.
        We will notify you of significant changes by email or by an in-platform notice.
      </p>

      <h2 style={s.h2}>Contact</h2>
      <p style={s.p}>
        For questions about this Cookie Policy, contact us at{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

    </LegalPage>
  );
}
